import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, KeyRound, Loader2, Lock, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AuthFrame from '../../components/layout/AuthFrame';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const {
    signIn,
    setNewPassword: submitNewPassword,
    user,
    organisation,
    loading: authLoading,
    orgSelectionRequired,
    allOrganisations,
    dataLoaded,
    newPasswordRequired,
    signOut,
  } = useAuth();
  const navigate = useNavigate();

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const { error: signInError } = await signIn(email, password);
    if (signInError) setError(signInError);
    setLoading(false);
  };

  const handleSetNewPassword = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    const { error: passwordError } = await submitNewPassword(newPassword);
    if (passwordError) setError(passwordError);
    setLoading(false);
  };

  useEffect(() => {
    if (authLoading || !user) return;
    if (orgSelectionRequired) { navigate('/select-org'); return; }
    if (organisation) {
      navigate('/app');
    } else if (dataLoaded && allOrganisations.length === 0) {
      signOut();
      setError('Your account is not a member of any organisation. Please contact your administrator.');
    } else if (!dataLoaded && !authLoading) {
      setError('Unable to load your account data. Please try signing in again.');
      setLoading(false);
    }
  }, [authLoading, user, organisation, orgSelectionRequired, dataLoaded, allOrganisations.length]);

  const errorMessage = error ? (
    <div className="mb-6 flex gap-3 border-l-2 border-app-critical bg-app-surface px-4 py-3" role="alert">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-app-critical" aria-hidden="true" />
      <p className="text-sm leading-5 text-app-ink">{error}</p>
    </div>
  ) : null;

  if (newPasswordRequired) {
    return (
      <AuthFrame eyebrow="Account security" title="Set your password" description="Your temporary password has expired. Choose a secure password to continue.">
        {errorMessage}
        <form onSubmit={handleSetNewPassword} className="space-y-5">
          <div>
            <label htmlFor="new-password" className="mb-2 block text-sm font-medium text-app-ink">New password</label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
              <input id="new-password" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={event => setNewPassword(event.target.value)} className="input-field !pl-11 !pr-11" placeholder="At least 8 characters" autoComplete="new-password" required />
              <button type="button" onClick={() => setShowNewPassword(value => !value)} className="app-focus-ring absolute right-1 top-1/2 inline-flex min-h-10 min-w-10 -translate-y-1/2 items-center justify-center text-app-muted hover:text-app-ink" aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}>
                {showNewPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="confirm-password" className="mb-2 block text-sm font-medium text-app-ink">Confirm password</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
              <input id="confirm-password" type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} className="input-field !pl-11" placeholder="Repeat your new password" autoComplete="new-password" required />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full gap-2">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Saving password</> : 'Set password and sign in'}
          </button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame eyebrow="Secure access" title="Sign in" description="Access your account intelligence, opportunity preparation and sales execution workspace.">
      {errorMessage}
      <form onSubmit={handleSignIn} className="space-y-5">
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-app-ink">Email address</label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
            <input id="email" type="email" value={email} onChange={event => setEmail(event.target.value)} className="input-field !pl-11" placeholder="you@company.com" autoComplete="email" required />
          </div>
        </div>
        <div>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <label htmlFor="password" className="block text-sm font-medium text-app-ink">Password</label>
            <Link to="/forgot-password" className="text-sm font-medium text-app-muted underline decoration-app-line-strong underline-offset-4 hover:text-app-ink hover:decoration-app-ink">Forgot your password?</Link>
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
            <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} className="input-field !pl-11 !pr-11" placeholder="Enter your password" autoComplete="current-password" required />
            <button type="button" onClick={() => setShowPassword(value => !value)} className="app-focus-ring absolute right-1 top-1/2 inline-flex min-h-10 min-w-10 -translate-y-1/2 items-center justify-center text-app-muted hover:text-app-ink" aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full gap-2">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Signing in</> : 'Sign in'}
        </button>
      </form>
      <p className="mt-8 flex flex-wrap items-center gap-x-2 border-t border-app-line pt-6 text-sm text-app-muted">Need access? <Link to="/signup" className="inline-flex min-h-10 items-center font-semibold text-app-ink underline decoration-app-line-strong underline-offset-4 hover:decoration-app-ink">Request an organisation account</Link></p>
    </AuthFrame>
  );
}
